/**
 * Classe de base abstraite partagée entre :
 *  - Step5IdentityVerificationComponent (Lilate / projet examination)
 *  - AIExamOnboardingStripeIdentityComponent (Elate/FlashLevel / projet ai)
 *
 * Contient toute la logique Stripe Identity :
 *  - Initialisation du SDK Stripe
 *  - Fetch du statut + réconciliation DB / Stripe
 *  - Polling automatique (toutes les 3s)
 *  - Ouverture/fermeture de la modal verifyIdentity()
 *  - Gestion session_does_not_exist / session_cancelled
 *  - Expiration de session sur le backend
 *  - Gestion des erreurs API (429, 403, 400, etc.)
 *
 * Les sous-classes implémentent uniquement :
 *  - callStartVerification() / callGetStatus() / callExpireSession() → appels API
 *  - onVerified() → action quand le statut atteint "verified" (une seule fois)
 *  - isInputReady() → vérifie si les inputs requis sont présents
 *  - stripePublicKey → clé publique Stripe (depuis l'environment du projet)
 *
 * Hooks d'observabilité (Sentry, analytics) : no-op par défaut, surchargés par Step5.
 */
import { Directive, OnInit, OnDestroy, signal, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { interval, Observable, Subscription } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { loadStripe } from '@stripe/stripe-js';
import { TranslateService } from '@ngx-translate/core';

import { IdentityVerificationService } from '../../service/identity-verification.service';
import {
  IdentityVerification,
  IdentityVerificationStatus,
  IdentityVerificationSessionResponse,
} from '../../model/identity-verification.model';

export const TERMINAL_STATUSES: IdentityVerificationStatus[] = [
  'verified', 'failed', 'definitively_failed', 'requires_input', 'expired'
];

@Directive()
export abstract class BaseStripeIdentityComponent implements OnInit, OnDestroy {

  protected identityService = inject(IdentityVerificationService);
  protected destroyRef = inject(DestroyRef);
  protected translateService = inject(TranslateService);

  // ─── State ───────────────────────────────────────────────────────────────────

  loading = signal(false);
  verificationStatus = signal<IdentityVerificationStatus>('pending');
  attemptsCount = signal(0);
  failureReason = signal<string | null>(null);
  errorMessage = signal('');

  /**
   * true pendant que la modal Stripe est ouverte.
   * Affiche un message "La vérification est en cours dans la fenêtre Stripe".
   */
  modalOpen = signal(false);

  // ─── Interne ─────────────────────────────────────────────────────────────────

  /** client_secret conservé pour pouvoir rouvrir la même session (requires_input) */
  protected clientSecret: string | null = null;

  private pollingSubscription: Subscription | null = null;

  /** Garde : onVerified() n'est appelé qu'une seule fois. */
  private alreadyCalledOnVerified = false;

  /** Instance Stripe initialisée une seule fois (évite les re-chargements). */
  protected stripeIdentity: {
    verifyIdentity: (clientSecret: string) => Promise<{ error?: { code?: string; message?: string } }>
  } | null = null;

  // ─── Abstract : à implémenter par les sous-classes ────────────────────────────

  /** Créer ou reprendre une session Stripe Identity. */
  protected abstract callStartVerification(): Observable<IdentityVerificationSessionResponse>;

  /** Récupérer le statut courant de la vérification. */
  protected abstract callGetStatus(): Observable<IdentityVerification>;

  /** Expirer la session courante sur le backend. */
  protected abstract callExpireSession(): Observable<unknown>;

  /** Déclenché une seule fois quand le statut atteint "verified". */
  protected abstract onVerified(): void;

  /** Vérifie si les inputs requis sont présents avant de charger le statut. */
  protected abstract isInputReady(): boolean;

  /** Clé publique Stripe (fournie par l'environment du projet consommateur). */
  protected abstract get stripePublicKey(): string | undefined;

  // ─── Hooks d'observabilité (no-op par défaut, surchargeable pour Sentry) ──────

  protected onStatusLoaded(_v: IdentityVerification): void {}
  protected onStatusFetchError(_err: unknown): void {}
  protected onVerificationStarted(): void {}
  protected onStartVerificationError(_err: { status?: number; error?: { detail?: string } }): void {}
  protected onBeforeOpenModal(): void {}
  protected onModalSessionCancelled(_errorCode: string): void {}
  protected onModalSessionDoesNotExist(_errorCode: string): void {}
  protected onModalOtherError(_error: { code?: string; message?: string }): void {}
  protected onModalException(_e: unknown): void {}
  protected onExpireSessionSuccess(): void {}
  protected onExpireSessionError(_err: unknown): void {}
  protected onPollingError(_err: unknown): void {}

  // ─── Comportement session_does_not_exist (surchargeable) ─────────────────────

  /**
   * Appelé quand Stripe retourne `session_does_not_exist`.
   * Défaut : expire la session sur le backend (l'utilisateur relance manuellement).
   * Step5 (Lilate) surcharge pour relancer automatiquement.
   */
  protected handleSessionDoesNotExist(): void {
    this.expireSessionOnBackend();
  }

  // ─── Lifecycle ───────────────────────────────────────────────────────────────

  ngOnInit(): void {
    this.initStripe();
    if (this.isInputReady()) {
      this.fetchStatus();
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  protected async initStripe(): Promise<void> {
    const stripeKey = this.stripePublicKey;
    if (!stripeKey) { return; }
    const stripe = await loadStripe(stripeKey);
    if (!stripe) { return; }
    const s = stripe as unknown as {
      verifyIdentity?: (clientSecret: string) => Promise<{ error?: { code?: string; message?: string } }>
    };
    if (typeof s.verifyIdentity === 'function') {
      this.stripeIdentity = s as typeof this.stripeIdentity;
    }
  }

  // ─── API ─────────────────────────────────────────────────────────────────────

  /**
   * Charge le statut courant depuis le backend.
   * Si status = processing : démarre le polling automatiquement.
   * Stocke le client_secret si le backend le renvoie (session requires_input existante).
   */
  protected fetchStatus(): void {
    this.loading.set(true);
    this.errorMessage.set('');

    this.callGetStatus()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (v) => {
          this.applyVerification(v);

          // On ne met à jour clientSecret que si la valeur renvoyée est non-nulle.
          const freshSecret = v.stripe_response?.['client_secret'];
          if (freshSecret) {
            this.clientSecret = freshSecret;
          }

          if (this.verificationStatus() === 'processing') {
            this.startPolling();
          }

          this.onStatusLoaded(v);
          this.loading.set(false);
        },
        error: (err) => {
          this.onStatusFetchError(err);
          this.loading.set(false);
        }
      });
  }

  protected applyVerification(v: IdentityVerification): void {
    // Réconciliation : si la DB est encore à "processing" mais que stripe_response
    // indique déjà un statut terminal (webhook en retard ou raté), on fait confiance à Stripe.
    let effectiveStatus = v.status;
    const stripeStatus = v.stripe_response?.['status'] as IdentityVerificationStatus | undefined;
    if (effectiveStatus === 'processing' && stripeStatus && TERMINAL_STATUSES.includes(stripeStatus)) {
      effectiveStatus = stripeStatus;
    }

    this.verificationStatus.set(effectiveStatus);
    this.attemptsCount.set(v.attempts_count ?? 0);
    this.failureReason.set(v.failure_reason || null);

    if (TERMINAL_STATUSES.includes(effectiveStatus)) {
      this.stopPolling();
    }

    if (effectiveStatus === 'verified' && !this.alreadyCalledOnVerified) {
      this.alreadyCalledOnVerified = true;
      this.onVerified();
    }
  }

  // ─── Actions publiques ────────────────────────────────────────────────────────

  /**
   * Première vérification (status = pending) ou nouvelle tentative.
   * POST /start-verification → client_secret → ouvre la modal.
   */
  startVerification(): void {
    this.loading.set(true);
    this.errorMessage.set('');

    this.callStartVerification()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: async (response) => {
          const secret = response.client_secret;
          if (!secret) {
            this.errorMessage.set(this.translateService.instant('examination-ai.step5.error.stripe-invalid'));
            this.loading.set(false);
            return;
          }
          this.onVerificationStarted();
          this.clientSecret = secret;
          this.loading.set(false);
          await this.openModal(secret);
        },
        error: (err) => this.handleApiError(err)
      });
  }

  /**
   * Reprend une session existante (status = requires_input ou processing).
   * Appelle start-verification qui retourne des credentials frais via
   * VerificationSession.retrieve() (HTTP 200) sans créer de nouvelle session.
   */
  resumeVerification(): void {
    this.clientSecret = null;
    this.startVerification();
  }

  /**
   * Nouvelle tentative après un échec (failed / expired).
   * Crée une nouvelle session Stripe (nouveau client_secret, nouveau compteur).
   */
  retryVerification(): void {
    if (!this.canRetry) {
      this.errorMessage.set(this.translateService.instant('examination-ai.step5.error.max-attempts'));
      return;
    }
    this.clientSecret = null;
    this.startVerification();
  }

  /**
   * Appelle le backend pour marquer la session comme expirée.
   * Déclenché par error.code = 'session_does_not_exist' de Stripe.
   */
  protected expireSessionOnBackend(): void {
    this.callExpireSession()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.verificationStatus.set('expired');
          this.clientSecret = null;
          this.onExpireSessionSuccess();
        },
        error: (err) => {
          this.onExpireSessionError(err);
          // Fallback : forcer l'état local même en cas d'erreur réseau
          this.verificationStatus.set('expired');
          this.clientSecret = null;
        }
      });
  }

  // ─── Modal Stripe ─────────────────────────────────────────────────────────────

  /**
   * Ouvre la modal Stripe Identity et gère tous les cas de fermeture.
   *
   * Fermeture possible :
   *  1. Soumis avec succès  → polling → verified
   *  2. Annulé / fermé      → session_cancelled → reste pending/requires_input
   *  3. Session invalide    → session_does_not_exist → handleSessionDoesNotExist()
   *  4. Erreur Stripe       → afficher l'erreur
   *
   * Dans TOUS les cas : fetchStatus() est appelé en finally.
   */
  protected async openModal(clientSecret: string): Promise<void> {
    if (!this.stripeIdentity) {
      await this.initStripe();
    }

    if (!this.stripeIdentity) {
      this.errorMessage.set(this.translateService.instant('examination-ai.step5.error.stripe-load'));
      return;
    }

    this.onBeforeOpenModal();
    this.modalOpen.set(true);
    this.errorMessage.set('');

    try {
      const result = await this.stripeIdentity.verifyIdentity(clientSecret);

      if (result?.error?.code === 'session_cancelled') {
        this.onModalSessionCancelled(result.error.code);
      } else if (result?.error?.code === 'session_does_not_exist') {
        this.onModalSessionDoesNotExist(result.error.code);
        this.modalOpen.set(false);
        this.handleSessionDoesNotExist();
        return;
      } else if (result?.error?.message && result?.error?.code !== 'session_cancelled') {
        this.onModalOtherError(result.error as { code?: string; message?: string });
        this.errorMessage.set(result.error.message);
      }
    } catch (e) {
      this.onModalException(e);
      this.errorMessage.set(this.translateService.instant('examination-ai.step5.error.unexpected'));
    } finally {
      this.modalOpen.set(false);
      this.fetchStatus();
    }
  }

  // ─── Polling ─────────────────────────────────────────────────────────────────

  private startPolling(): void {
    if (this.pollingSubscription) { return; }

    this.pollingSubscription = interval(3000).pipe(
      takeUntilDestroyed(this.destroyRef),
      switchMap(() => this.callGetStatus()),
      takeWhile(
        (v) => !TERMINAL_STATUSES.includes(v.status),
        true // inclusive : émettre la dernière valeur terminale avant de stopper
      )
    ).subscribe({
      next: (v) => {
        this.applyVerification(v);
        const freshSecret = v.stripe_response?.['client_secret'];
        if (freshSecret) {
          this.clientSecret = freshSecret;
        }
      },
      error: (err) => this.onPollingError(err)
    });
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = null;
  }

  // ─── Gestion erreurs API ──────────────────────────────────────────────────────

  protected handleApiError(err: { status?: number; error?: { detail?: string } }): void {
    this.onStartVerificationError(err);
    if (err.status === 429) {
      this.errorMessage.set(this.translateService.instant('examination-ai.step5.error.too-many'));
    } else if (err.status === 403) {
      this.errorMessage.set(this.translateService.instant('examination-ai.step5.error.forbidden'));
    } else if (err.status === 400 && err.error?.detail?.includes('expired')) {
      this.errorMessage.set(this.translateService.instant('examination-ai.step5.error.session-expired'));
    } else {
      this.errorMessage.set(this.translateService.instant('examination-ai.step5.error.start-failed'));
    }
    this.loading.set(false);
  }

  // ─── Computed getters ────────────────────────────────────────────────────────

  /** Reprendre une session Stripe existante (requires_input ou processing interrompu) */
  get canResume(): boolean {
    if (this.isBlocked) { return false; }
    return ['requires_input', 'processing'].includes(this.verificationStatus());
  }

  /** Nouvelle tentative possible (failed ou expired, moins de 2 essais) */
  get canRetry(): boolean {
    if (this.isBlocked) { return false; }
    const retryableStatuses: IdentityVerificationStatus[] = ['failed', 'expired'];
    return retryableStatuses.includes(this.verificationStatus()) && this.attemptsCount() < 2;
  }

  /** Échec définitif ou session bloquée (processing/expired/failed avec max tentatives) */
  get isBlocked(): boolean {
    if (this.verificationStatus() === 'definitively_failed') { return true; }
    return this.attemptsCount() >= 2
      && ['processing', 'expired', 'failed'].includes(this.verificationStatus());
  }
}
